/**
 * Федеративный вход: GitHub и Google (docs/adr/0008-federated-login.md).
 *
 * Клиент не участвует в самом протоколе OAuth: он просит у сервера адрес,
 * уводит туда браузер и на этом его роль заканчивается. Обратно пользователь
 * возвращается уже с проставленной refresh-cookie, а access-токен подтянется
 * обычным обновлением сессии — токенов в адресной строке нет.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiFetch } from './client'
import { authFetch } from './session'

export const providerSchema = z.enum(['github', 'google'])
export type OAuthProvider = z.infer<typeof providerSchema>

const providersSchema = z.object({
  // Неизвестные имена отбрасываем: фронтенд умеет рисовать только то,
  // для чего у него есть название и иконка.
  providers: z.array(z.string()).transform((names) => names.filter(isKnownProvider)),
})

const identitySchema = z.object({
  provider: providerSchema,
  email: z.string().nullable(),
  created_at: z.string(),
})

const loginMethodsSchema = z.object({
  has_password: z.boolean(),
  identities: z.array(identitySchema),
})

export type Identity = z.infer<typeof identitySchema>
export type LoginMethods = z.infer<typeof loginMethodsSchema>

export const providersKey = ['auth', 'oauth-providers'] as const
export const loginMethodsKey = ['auth', 'identities'] as const

function isKnownProvider(name: string): name is OAuthProvider {
  return providerSchema.safeParse(name).success
}

export async function fetchProviders(): Promise<OAuthProvider[]> {
  const response = await apiFetch<unknown>('/auth/oauth/providers')
  return providersSchema.parse(response).providers
}

/** Список настроенных провайдеров. Пустой — кнопок не будет вовсе. */
export function useOAuthProviders() {
  return useQuery({
    queryKey: providersKey,
    queryFn: fetchProviders,
    // Набор провайдеров меняется только с конфигурацией сервера.
    staleTime: Infinity,
    retry: false,
  })
}

const startSchema = z.object({ authorization_url: z.string() })

async function startFlow(path: string, next: string | undefined, authorized: boolean) {
  const body = JSON.stringify({ next: next ?? null })
  const response = authorized
    ? await authFetch<unknown>(path, { method: 'POST', body })
    : await apiFetch<unknown>(path, { method: 'POST', body })
  return startSchema.parse(response).authorization_url
}

export function beginLogin(provider: OAuthProvider, next?: string): Promise<string> {
  return startFlow(`/auth/oauth/${provider}/start`, next, false)
}

export function beginLink(provider: OAuthProvider, next?: string): Promise<string> {
  return startFlow(`/auth/oauth/${provider}/link`, next, true)
}

/**
 * Уводит браузер на страницу провайдера. Вынесено отдельной функцией,
 * чтобы в тестах подменять переход, а не настоящую навигацию.
 */
export function navigateTo(url: string): void {
  window.location.assign(url)
}

export function useOAuthLogin(next?: string) {
  return useMutation({
    mutationFn: (provider: OAuthProvider) => beginLogin(provider, next),
    onSuccess: navigateTo,
  })
}

export function useOAuthLink(next?: string) {
  return useMutation({
    mutationFn: (provider: OAuthProvider) => beginLink(provider, next),
    onSuccess: navigateTo,
  })
}

export async function fetchLoginMethods(): Promise<LoginMethods> {
  return loginMethodsSchema.parse(await authFetch<unknown>('/auth/identities'))
}

export function useLoginMethods() {
  return useQuery({ queryKey: loginMethodsKey, queryFn: fetchLoginMethods, retry: false })
}

export async function unlinkProvider(provider: OAuthProvider): Promise<void> {
  await authFetch<void>(`/auth/identities/${provider}`, { method: 'DELETE' })
}

export function useUnlinkProvider() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: unlinkProvider,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: loginMethodsKey }),
  })
}
